/* ==========================================================================
   PEMA – Produkt-Tabs (sections/pema-produkt-tabs.liquid)

   Uebernimmt die Tab-Steuerung inklusive Tastaturbedienung nach dem
   WAI-ARIA Tabs-Pattern (automatische Aktivierung beim Pfeiltasten-Wechsel).
   Die Sichtbarkeit der Panels regelt das CSS ueber die Klasse `is-active`;
   ohne dieses Skript bleibt die Tab-Leiste verborgen und alle Panels sind
   untereinander lesbar.
   ========================================================================== */
(function () {
  'use strict';

  // Muss ganz am Anfang stehen und nicht erst in einem DOMContentLoaded-
  // Handler: Erst diese Klasse schaltet das CSS von "alle Panels untereinander"
  // auf Tab-Darstellung um. Faellt dieses Asset aus (CDN, Adblocker, CSP) oder
  // bricht es beim Parsen ab, bleibt die Klasse weg – dann ist die Reiterleiste
  // ausgeblendet und jeder Panel-Inhalt weiterhin erreichbar.
  document.documentElement.classList.add('pema-tabs-js');

  var KEYS = {
    left: 'ArrowLeft',
    right: 'ArrowRight',
    up: 'ArrowUp',
    down: 'ArrowDown',
    home: 'Home',
    end: 'End'
  };

  function PemaTabs(root) {
    this.root = root;
    this.bar = root.querySelector('.pema-tabs__bar');
    this.track = root.querySelector('[data-pema-track]');
    this.indicator = root.querySelector('[data-pema-indicator]');
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-pema-tab]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-pema-panel]'));

    if (this.tabs.length < 2 || this.tabs.length !== this.panels.length) {
      return;
    }

    this.index = Math.max(0, this.tabs.indexOf(root.querySelector('[data-pema-tab].is-active')));

    this.bindEvents();
    this.activate(this.index, false);
    this.syncIndicator();
    this.syncOverflow();

    // Nach dem Laden der Webfonts aendern sich die Reiterbreiten.
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(this.refresh.bind(this));
    }

    // Der Balken darf erst nach der ersten Positionierung animieren,
    // sonst faehrt er beim Seitenaufbau sichtbar von links ein.
    var self = this;
    window.requestAnimationFrame(function () {
      if (self.indicator) {
        self.indicator.classList.add('is-ready');
      }
    });
  }

  PemaTabs.prototype.bindEvents = function () {
    var self = this;

    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        self.activate(i, true);
      });
    });

    this.track.addEventListener('keydown', function (event) {
      var last = self.tabs.length - 1;
      var next = null;

      // Ausgangspunkt ist der fokussierte Reiter; nur wenn der Fokus
      // ausserhalb liegt, greift der zuletzt aktivierte Index.
      var from = self.tabs.indexOf(document.activeElement);
      if (from < 0) {
        from = self.index;
      }

      if (event.key === KEYS.right || event.key === KEYS.down) {
        next = from === last ? 0 : from + 1;
      } else if (event.key === KEYS.left || event.key === KEYS.up) {
        next = from === 0 ? last : from - 1;
      } else if (event.key === KEYS.home) {
        next = 0;
      } else if (event.key === KEYS.end) {
        next = last;
      }

      if (next === null) {
        return;
      }

      event.preventDefault();
      self.activate(next, true);
      self.tabs[next].focus();
    });

    if (this.bar) {
      this.bar.addEventListener('scroll', this.syncOverflow.bind(this), { passive: true });
    }

    if (window.ResizeObserver) {
      this.observer = new window.ResizeObserver(this.refresh.bind(this));
      this.observer.observe(this.root);
    } else {
      window.addEventListener('resize', this.refresh.bind(this));
    }
  };

  PemaTabs.prototype.activate = function (index, moveIntoView) {
    var self = this;
    this.index = index;

    this.tabs.forEach(function (tab, i) {
      var active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.panels.forEach(function (panel, i) {
      var active = i === index;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.setAttribute('tabindex', '0');
      } else {
        panel.removeAttribute('tabindex');
      }
    });

    this.syncIndicator();

    if (moveIntoView && this.bar && this.bar.scrollWidth > this.bar.clientWidth) {
      var tab = this.tabs[index];
      var left = tab.offsetLeft - (this.bar.clientWidth - tab.offsetWidth) / 2;
      this.bar.scrollTo({
        left: Math.max(0, left),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      window.setTimeout(function () {
        self.syncOverflow();
      }, 350);
    }
  };

  PemaTabs.prototype.syncIndicator = function () {
    if (!this.indicator) {
      return;
    }
    var tab = this.tabs[this.index];
    if (!tab) {
      return;
    }
    this.indicator.style.width = tab.offsetWidth + 'px';
    this.indicator.style.transform = 'translateX(' + tab.offsetLeft + 'px)';
  };

  PemaTabs.prototype.syncOverflow = function () {
    if (!this.bar) {
      return;
    }
    var overflowing = this.bar.scrollWidth - this.bar.clientWidth > 1;
    this.bar.classList.toggle('is-overflowing', overflowing);
    this.bar.classList.toggle(
      'is-scrolled-end',
      overflowing && this.bar.scrollLeft + this.bar.clientWidth >= this.bar.scrollWidth - 1
    );
  };

  PemaTabs.prototype.refresh = function () {
    this.syncIndicator();
    this.syncOverflow();
  };

  function init(scope) {
    var container = scope || document;
    var roots = container.querySelectorAll('[data-pema-tabs]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.pemaTabsInit === 'true') {
        return;
      }
      root.dataset.pemaTabsInit = 'true';
      new PemaTabs(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme-Editor: Sektion wurde neu gerendert
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
